import { Icon } from "@/components/primitives";
import {
  EVIDENCE_CHECKLIST,
  FLAG_LABEL,
  resolveChecklist,
  type EvidenceFlag,
  type EvidenceStatus,
} from "@/lib/evidenceChecklist";

interface EvidenceChecklistProps {
  flag: EvidenceFlag;
  /** Free-text "what Fig saw" lines from the AI verification block. */
  evidence?: string[];
  /** Free-text "still needed" prompts from the AI verification block. */
  prompts?: string[];
  /**
   * When true, every shot renders with status "optional" regardless of
   * `evidence`/`prompts`. Used by the standalone Best-Practices guide on
   * /best-practices where there is no live scan.
   */
  reference?: boolean;
  /** Hide the section's category-name heading (caller already has one). */
  hideHeading?: boolean;
}

/**
 * Structured checklist of the ideal evidence shots for a verification-
 * sensitive category. Each row shows: status icon, shot label, and a
 * short "why this matters" line. Status is derived by resolveChecklist().
 */
export function EvidenceChecklist({
  flag,
  evidence = [],
  prompts = [],
  reference = false,
  hideHeading = false,
}: EvidenceChecklistProps) {
  const rows = reference
    ? EVIDENCE_CHECKLIST[flag].map((shot) => ({ shot, status: "optional" as EvidenceStatus }))
    : resolveChecklist(flag, evidence, prompts);

  const providedCount = rows.filter((r) => r.status === "provided").length;
  const missingCount  = rows.filter((r) => r.status === "missing").length;

  return (
    <div>
      {!hideHeading && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          marginBottom: 6,
        }}>
          <div className="caps" style={{ color: "hsl(var(--rust))" }}>
            {FLAG_LABEL[flag]} · evidence checklist
          </div>
          {!reference && (
            <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 9 }}>
              {providedCount}/{rows.length} confirmed
            </div>
          )}
        </div>
      )}
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {rows.map(({ shot, status }, i) => (
          <li key={shot.label} style={{
            display: "flex", gap: 10, padding: "10px 0",
            borderTop: i > 0 ? "1px dashed hsl(var(--sand-deep))" : "none",
            alignItems: "flex-start",
          }}>
            <StatusBox status={status} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                gap: 8,
              }}>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                  color: "hsl(var(--ink))", lineHeight: 1.2,
                }}>
                  {shot.label}
                </div>
                <StatusBadge status={status} />
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.35, marginTop: 2,
              }}>
                {shot.why}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {!reference && missingCount > 0 && (
        <div style={{
          marginTop: 10, padding: "8px 10px",
          background: "hsl(var(--paper-3))", border: "1px dashed hsl(var(--rust))",
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          letterSpacing: "0.08em", color: "hsl(var(--ink))", lineHeight: 1.4,
        }}>
          {missingCount} required shot{missingCount === 1 ? "" : "s"} missing — add them to upgrade to photo-verified.
        </div>
      )}
    </div>
  );
}

function StatusBox({ status }: { status: EvidenceStatus }) {
  const color =
    status === "provided" ? "hsl(var(--olive))" :
    status === "missing"  ? "hsl(var(--rust))"  :
                            "hsl(var(--ink-2))";
  return (
    <div style={{
      width: 18, height: 18, flexShrink: 0,
      border: `1.5px solid ${color}`,
      background: status === "provided" ? color : "transparent",
      color: "hsl(var(--cream))",
      display: "flex", alignItems: "center", justifyContent: "center",
      marginTop: 2,
    }}>
      {status === "provided" && Icon.check(12)}
      {status === "missing" && (
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700,
          color,
        }}>!</span>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: EvidenceStatus }) {
  const map: Record<EvidenceStatus, { label: string; color: string }> = {
    provided: { label: "Confirmed",   color: "hsl(var(--olive))" },
    missing:  { label: "Needed",      color: "hsl(var(--rust))"  },
    optional: { label: "Recommended", color: "hsl(var(--ink-2))" },
  };
  const { label, color } = map[status];
  return (
    <span style={{
      flexShrink: 0,
      fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
      letterSpacing: "0.12em", textTransform: "uppercase",
      color,
    }}>
      {label}
    </span>
  );
}